import { FC, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UploadFile } from "antd/es/upload/interface";
import { useGetProductById } from "hooks/useProduct";
import { useAppSelector } from "store";
import { toMerchantIdArray } from "utils";
import type { PriceProductByIdResponse } from "services/interface";
import { SkuProductModal } from "./CreateProduct/ProductSKUCard";
import { SkuModalStockEditProvider } from "./CreateProduct/ProductSKUCreate/SkuModalStockEditContext";

interface InventorySkuEditModalProps {
  /** สินค้าเจ้าของ SKU */
  productId: string;
  /** SKU ที่ต้องการแก้ไข (product_sku_variant id) */
  skuId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Host เปิด modal "กำหนดสินค้า — ราคาและคลัง" (โหมดคลัง) แบบ inline จากตารางสต็อก SKU
 * - โหลดสินค้าด้วย getProductById แล้ว map เป็น variants/prices เหมือนหน้าแก้ไขสินค้า
 * - เปิด modal ในโหมดแก้ไข + เฉพาะแท็บคลัง (warehouseOnly) ของ SKU ที่เลือก
 * - บันทึกเสร็จ refetch product + inventory ให้ตารางอัปเดต
 */
const InventorySkuEditModal: FC<InventorySkuEditModalProps> = ({
  productId,
  skuId,
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id ?? "",
  );
  const { data } = useGetProductById(productId);

  const [variants, setVariants] = useState<VariantsInput>([]);
  const [prices, setPrices] = useState<PricesInput>([]);
  const [originalVariants, setOriginalVariants] = useState<VariantsInput>([]);
  const [originalPrices, setOriginalPrices] = useState<PricesInput>([]);
  const [skuImage, setSkuImage] = useState<UploadFile[] | undefined>(undefined);

  // map product → variants/prices (โครงสร้างเดียวกับหน้าแก้ไขสินค้า: 1 SKU = 1 variant)
  useEffect(() => {
    if (!data?.product) return;

    const mappedVariants: VariantsInput = [];
    data.product.variants.forEach((variant) => {
      variant.items.forEach((item) => {
        mappedVariants.push({
          id: variant.id,
          name: variant.name,
          capacity: item.product_capacity,
          items: [
            {
              id: item.id,
              name: item.name,
              image_id: item.image_id,
              image_url: item.image_url,
              product_code: item.product_code || "",
              product_capacity: item.product_capacity,
              product_quantity: item.product_quantity,
              lead_time: item.lead_time,
              preparation_time: item.preparation_time || 1,
              width: item.width || 0,
              length: item.length || 0,
              height: item.height || 0,
              weight: item.weight || 0,
              packaging_type_id: item.packaging_type_id || null,
              shipping_method:
                item.shipping_method ??
                ((item.width || 0) > 0 ||
                (item.length || 0) > 0 ||
                (item.height || 0) > 0 ||
                (item.weight || 0) > 0
                  ? "bangbow"
                  : "self"),
              is_active: item.is_active !== undefined ? item.is_active : true,
              is_draft: item.is_draft || false,
              merchants: toMerchantIdArray(item.merchants),
              merchant_id: item.merchant_id || "",
              is_stock: item.is_stock ?? false,
              stock:
                item.stock ?? {
                  stock_quantity: 0,
                  holding_quantity: 0,
                  stock_balance: 0,
                },
            },
          ],
        });
      });
    });

    const apiPrices: PricesInput = [];
    data.product.prices.forEach((item: PriceProductByIdResponse) => {
      apiPrices.push({
        id: item.id,
        variant_1_item_id: item.variant_1_item_id,
        variant_2_item_id: item.variant_2_item_id,
        factory_price: Number(item.factory_price),
        retail_price: Number(item.retail_price),
        wholesale_price: Number(item.wholesale_price),
        vip_price: Number(item.vip_price),
        base_factory_price: item.base_factory_price || 0,
        base_wholesale_price: item.base_wholesale_price || 0,
        base_retail_price: item.base_retail_price || 0,
        base_vip_price: item.base_vip_price || 0,
        transport_price: item.transport_price || 0,
        api_transport_carrier_code: item.api_transport_carrier_code || undefined,
        api_transport_logo: item.api_transport_logo || undefined,
        api_transport_price: Number(item.api_transport_price ?? 0),
        add_on_api_transport_price: Number(item.add_on_api_transport_price ?? 0),
        self_transport_price: Number(item.self_transport_price ?? 0),
        percent_payment_gateway: item.percent_payment_gateway || 3,
        payment_getway: item.payment_getway || 0,
        commistion_lv1: item.commistion_lv1 ?? undefined,
        commistion_lv2: item.commistion_lv2 ?? undefined,
        commistion_lv3: item.commistion_lv3 ?? undefined,
        commistion_lv4: item.commistion_lv4 ?? undefined,
        commistion_lv5: item.commistion_lv5 ?? undefined,
        total_commission: item.total_commission ?? undefined,
        unit_factory_price: String(item.unit_factory_price || "0"),
        unit_wholesale_price: String(item.unit_wholesale_price || "0"),
        unit_retail_price: String(item.unit_retail_price || "0"),
        unit_vip_price: String(item.unit_vip_price || "0"),
        ...(item.quantity_per_unit != null
          ? { quantity_per_unit: Number(item.quantity_per_unit) }
          : {}),
        ...(item.product_unit != null && item.product_unit !== ""
          ? { product_unit: item.product_unit }
          : {}),
        ...((item as any).sku_tags ? { sku_tags: (item as any).sku_tags } : {}),
      });
    });

    setVariants(mappedVariants);
    setOriginalVariants(mappedVariants);
    setPrices(apiPrices);
    setOriginalPrices(apiPrices);
  }, [data]);

  // หา variant + price ของ SKU ที่จะแก้ไข (โครงสร้าง 1 SKU = 1 variant)
  const editData = useMemo(() => {
    const idx = variants.findIndex((v) => v.items?.[0]?.id === skuId);
    if (idx === -1) return null;
    const variant = variants[idx];
    const price =
      prices.find((p) => p.variant_1_item_id === skuId) ??
      ({ variant_1_item_id: skuId } as Price);
    return { variant, price, index: idx };
  }, [variants, prices, skuId]);

  const factoryIdForProduct =
    data?.product?.factory?.[0]?.factory_id ?? factoryId;

  // refetch ให้ตารางคลังอัปเดตหลังแก้ไข
  const refreshAfterChange = () => {
    queryClient.refetchQueries({ queryKey: ["product", productId], type: "all" });
    if (factoryId) {
      queryClient.invalidateQueries({ queryKey: ["inventoryByFactory", factoryId] });
    }
  };

  const handleClose = () => {
    refreshAfterChange();
    onClose();
  };

  // ยังโหลดข้อมูลไม่เสร็จ / ไม่พบ SKU → ยังไม่เปิด modal
  if (!editData) return null;

  return (
    <SkuModalStockEditProvider
      productId={productId}
      open={open}
      initialLayoutTab="warehouse"
    >
      <SkuProductModal
        open={open}
        onClose={handleClose}
        warehouseOnly
        setVariants={setVariants}
        variants={variants}
        setPrices={setPrices}
        prices={prices}
        copyMode={false}
        editMode
        copyData={null}
        editData={editData}
        skuImage={skuImage}
        setSkuImage={setSkuImage}
        originalPrices={originalPrices}
        originalVariants={originalVariants}
        factoryId={factoryIdForProduct}
        isEditMode
        productId={productId}
        percentCommission={0}
        categoryName={""}
        onStockToggled={refreshAfterChange}
      />
    </SkuModalStockEditProvider>
  );
};

export default InventorySkuEditModal;
